import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Loader2 } from 'lucide-react';

/**
 * MetadataEditModal - Edit document metadata
 */
const MetadataEditModal = ({
  isOpen,
  onClose,
  editingMetadata,
  setEditingMetadata,
  documentTypes,
  handleSaveMetadata,
  savingMetadata,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-slate-100">Edit Document Metadata</DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400">
            Update the metadata to ensure proper filing and indexing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Document Type *</Label>
              <Select value={editingMetadata.document_type} onValueChange={(v) => setEditingMetadata(prev => ({ ...prev, document_type: v }))}>
                <SelectTrigger className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                  {documentTypes.map(type => (
                    <SelectItem key={type} value={type} className="text-gray-700 hover:bg-slate-200">{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Document Date *</Label>
              <Input type="date" value={editingMetadata.document_date} onChange={(e) => setEditingMetadata(prev => ({ ...prev, document_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Effective Date</Label>
              <Input type="date" value={editingMetadata.effective_date} onChange={(e) => setEditingMetadata(prev => ({ ...prev, effective_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Filing Date</Label>
              <Input type="date" value={editingMetadata.filing_date} onChange={(e) => setEditingMetadata(prev => ({ ...prev, filing_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Receipt Date</Label>
              <Input type="date" value={editingMetadata.receipt_date} onChange={(e) => setEditingMetadata(prev => ({ ...prev, receipt_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Site</Label>
              <Input value={editingMetadata.site} onChange={(e) => setEditingMetadata(prev => ({ ...prev, site: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Country</Label>
              <Input value={editingMetadata.country} onChange={(e) => setEditingMetadata(prev => ({ ...prev, country: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Owner</Label>
              <Input value={editingMetadata.owner} onChange={(e) => setEditingMetadata(prev => ({ ...prev, owner: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Contact</Label>
              <Input value={editingMetadata.contact} onChange={(e) => setEditingMetadata(prev => ({ ...prev, contact: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Artifact Classification</Label>
              <Input value={editingMetadata.artifact_classification} onChange={(e) => setEditingMetadata(prev => ({ ...prev, artifact_classification: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">Keywords (comma-separated)</Label>
            <Input value={editingMetadata.keywords} onChange={(e) => setEditingMetadata(prev => ({ ...prev, keywords: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">Description</Label>
            <Input value={editingMetadata.description} onChange={(e) => setEditingMetadata(prev => ({ ...prev, description: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} className="rounded-full bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-100">Cancel</Button>
          <Button onClick={handleSaveMetadata} disabled={savingMetadata} className="rounded-full bg-red-600 hover:bg-red-700 text-slate-900 dark:text-slate-100">
            {savingMetadata ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Metadata'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MetadataEditModal;
